import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { NormalizedUIField } from '../../../store/config.models';
import { ButtonDisableIdentifier } from '../../../store/models/validator-identifier';
import * as fromSelectors from '../../../store/selectors';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

enum DividerClass {
    Base = 'divider-element',
    Disabled = 'divider-disabled',
    Enabled = 'divider-enabled',
    Vertical = 'divider-vertical',
    Horizontal = 'divider-horizontal',
}

enum DividerThicknessClass {
    Thin = 'divider-thin',
    Medium = 'divider-medium',
    Thick = 'divider-thick',
}

enum CssProperty {
    BackgroundColor = 'background-color',
}

@Component({
    selector: 'app-divider-element',
    templateUrl: './divider-element.component.html',
    styleUrls: ['./divider-element.component.scss'],
})
export class DividerElementComponent implements OnInit {
    @Input() field: NormalizedUIField;
    @Input() isReadOnly = false;

    isDisabled$: Observable<boolean> = of(false);
    dividerColorClass$: Observable<string>;

    // Make enums available in the template
    DividerClass = DividerClass;
    CssProperty = CssProperty;

    constructor(private store: Store) {}

    ngOnInit(): void {
        if (this.field.disabledSelector) {
            switch (this.field.disabledSelector) {
                case ButtonDisableIdentifier.invalidOrPristine:
                    this.isDisabled$ = this.store.select(fromSelectors.selectFormInvalidOrPristine);
                    break;
                case ButtonDisableIdentifier.invalid:
                    this.isDisabled$ = this.store.select(fromSelectors.selectFormInvalid);
                    break;
                case ButtonDisableIdentifier.pristine:
                    this.isDisabled$ = this.store.select(fromSelectors.selectFormPristine);
                    break;
                case ButtonDisableIdentifier.invalidOrUnsaved:
                    this.isDisabled$ = this.store.select(fromSelectors.selectFormInvalidOrUnsaved);
                    break;
                default:
                    this.isDisabled$ = of(!!this.field.disabled);
            }
        } else {
            this.isDisabled$ = of(!!this.field.disabled);
        }

        // Generate color class based on disabledSelector presence
        this.isDisabled$ = this.isDisabled$.pipe(map((isDisabled) => isDisabled || this.isReadOnly));

        this.dividerColorClass$ = this.isDisabled$.pipe(
            map((isDisabled) => {
                if (!this.field.disabledSelector) {
                    return '';
                }

                return isDisabled ? DividerClass.Disabled : DividerClass.Enabled;
            }),
        );
    }

    get isVisible(): boolean {
        return this.field?.visible !== false;
    }

    get isVertical(): boolean {
        return this.field?.panelClass === DividerClass.Vertical;
    }

    get orientationClass(): string {
        return this.isVertical ? DividerClass.Vertical : DividerClass.Horizontal;
    }

    getDividerThicknessClass(): string {
        const thickness = this.field.dividerThickness || 1;
        if (thickness <= 1) return DividerThicknessClass.Thin;
        if (thickness <= 2) return DividerThicknessClass.Medium;
        return DividerThicknessClass.Thick;
    }

    getDividerStyleClass(): string {
        return this.field.dividerStyle || 'solid';
    }

    getStyleObject(): Record<string, string> {
        if (!this.field.disabledSelector && this.field.dividerColor) {
            return { [CssProperty.BackgroundColor]: this.field.dividerColor };
        }
        return {};
    }
}
